import { ExclamationCircleOutlined, PlusOutlined } from '@ant-design/icons'
import { Button, Form, FormItem, Heading, TextArea } from '@lmc/ui-kit'
import { Text } from '@lmc/ui-kit/src/general'
import { Form as AntdForm, Divider, Modal, Select } from 'antd'
import { useEffect, useState } from 'react'
import styled from 'styled-components'
import useAbilityAccess from '../../../../../components/AccessControl/useAbilityAccess'
import { ALERT_CATEGORY_LABELS, ALERT_STATUS_LABELS } from '../constants'
import { useAlertCreate } from '../hooks/useAlertCreate'
import { useAlertDelete } from '../hooks/useAlertDelete'
import { useAlertUpdate } from '../hooks/useAlertUpdate'
import type { Alert, AlertCategory, AlertStatus } from '../utils/types'

export type AlertModalProps = {
  open: boolean
  onClose: () => void
  residentId: string
  initialAlert?: Alert | null
}

const CATEGORY_OPTIONS = Object.entries(ALERT_CATEGORY_LABELS).map(
  ([value, label]) => ({ value, label })
)

const STATUS_OPTIONS = Object.entries(ALERT_STATUS_LABELS).map(
  ([value, label]) => ({ value, label })
)

const AlertModal = ({
  open,
  onClose,
  residentId,
  initialAlert
}: AlertModalProps) => {
  const [form] = AntdForm.useForm()
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const isEditMode = !!initialAlert

  const canDeleteAlert = useAbilityAccess([{ i: 'delete', this: 'Alert' }])
  const { createAlert, isCreateLoading } = useAlertCreate({
    onSuccess: onClose
  })
  const { updateAlert, isUpdateLoading } = useAlertUpdate({
    onSuccess: onClose
  })
  const { deleteAlert, isDeleteLoading } = useAlertDelete({
    onSuccess: onClose
  })

  useEffect(() => {
    if (open) {
      setShowDeleteConfirm(false)
      if (initialAlert) {
        form.setFieldsValue({
          category: initialAlert.category,
          description: initialAlert.description,
          status: initialAlert.status,
          statusNote: undefined
        })
      } else {
        form.resetFields()
        form.setFieldsValue({ status: 'active' })
      }
    }
  }, [open, initialAlert, form])

  const handleSubmit = async (values: {
    category: AlertCategory
    description: string
    status: AlertStatus
    statusNote?: string
  }) => {
    if (isEditMode && initialAlert) {
      await updateAlert({
        id: initialAlert.id,
        category: values.category,
        description: values.description,
        status: values.status,
        statusNote: values.statusNote
      })
    } else {
      await createAlert({
        residentId,
        category: values.category,
        description: values.description,
        status: values.status
      })
    }
  }

  return (
    <>
      <Modal
        open={open}
        onCancel={onClose}
        footer={
          <ModalFooter>
            {isEditMode && canDeleteAlert && (
              <Button
                variant="danger"
                onClick={() => setShowDeleteConfirm(true)}
              >
                Delete
              </Button>
            )}
            <Button onClick={onClose}>Cancel</Button>
            <Button
              onClick={form.submit}
              variant="primary"
              isLoading={isEditMode ? isUpdateLoading : isCreateLoading}
            >
              {isEditMode ? (
                'Save'
              ) : (
                <>
                  <PlusOutlined />
                  Add alert
                </>
              )}
            </Button>
          </ModalFooter>
        }
        width={600}
      >
        <Heading level={3}>{isEditMode ? 'Edit alert' : 'Add alert'}</Heading>
        <Divider />
        <StyledForm form={form} onFinish={handleSubmit} layout="horizontal">
          <FormItem
            name="category"
            label={<Text>Category</Text>}
            rules={[{ required: true, message: 'Category is required' }]}
          >
            <Select placeholder="Select category">
              {CATEGORY_OPTIONS.map(({ value, label }) => (
                <Select.Option key={value} value={value}>
                  {label}
                </Select.Option>
              ))}
            </Select>
          </FormItem>

          <FormItem
            name="description"
            label={<Text>Description</Text>}
            rules={[{ required: true, message: 'Description is required' }]}
          >
            <TextArea rows={4} placeholder="Describe the alert" />
          </FormItem>

          <FormItem
            name="status"
            label={<Text>Status</Text>}
            rules={[{ required: true, message: 'Status is required' }]}
          >
            <Select placeholder="Select status">
              {STATUS_OPTIONS.map(({ value, label }) => (
                <Select.Option key={value} value={value}>
                  {label}
                </Select.Option>
              ))}
            </Select>
          </FormItem>

          {isEditMode && (
            <FormItem name="statusNote" label={<Text>Status note</Text>}>
              <TextArea
                rows={2}
                placeholder="Optional reason for status change"
              />
            </FormItem>
          )}

          <Divider />
        </StyledForm>
      </Modal>

      {initialAlert && (
        <StyledConfirmModal
          open={showDeleteConfirm}
          onCancel={() => setShowDeleteConfirm(false)}
          closable
          width={600}
          title={
            <ModalTitleWrapper>
              <ExclamationCircleOutlined
                style={{ color: '#fa8c16', fontSize: 22 }}
              />
              <ModalTitleText level={3}>Delete alert</ModalTitleText>
            </ModalTitleWrapper>
          }
          footer={
            <ConfirmFooter>
              <Button onClick={() => setShowDeleteConfirm(false)}>
                Cancel
              </Button>
              <Button
                variant="danger"
                isLoading={isDeleteLoading}
                onClick={() => deleteAlert(initialAlert.id)}
              >
                Delete
              </Button>
            </ConfirmFooter>
          }
        >
          <ModalBody>
            <Text level={1}>
              Are you sure you want to delete this alert? This action cannot be
              undone.
            </Text>
          </ModalBody>
          <Divider />
        </StyledConfirmModal>
      )}
    </>
  )
}

const StyledForm = styled(Form)`
  display: flex;
  flex-direction: column;
  gap: 16px;
`

const ModalFooter = styled.div`
  display: flex;
  gap: 8px;
  margin-left: auto;
`

const StyledConfirmModal = styled(Modal)`
  .ant-modal-header {
    border-bottom: 1px solid rgba(0, 0, 0, 0.06);
  }
  .ant-modal-title {
    text-transform: none;
  }
  .ant-modal-footer {
    border-top: 1px solid rgba(0, 0, 0, 0.06);
  }
`

const ModalTitleWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`

const ModalTitleText = styled(Heading)`
  color: ${({ theme }) => theme.color?.neutral?.color3};
  font-weight: 500;
  text-transform: none;
`

const ModalBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`

const ConfirmFooter = styled.div`
  display: flex;
  gap: 8px;
  justify-content: flex-end;
`

export default AlertModal
